import Header from 'components/Headers/Header';
import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Container,
  Row,
  Table,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import {
  getWhatsappMessages,
  scheduleWhatsappMessage,
  cancelWhatsappMessage,
  sendWhatsappMessageNow,
  retryWhatsappMessage,
  retryAllFailedMessages,
} from 'services/whatsapp';
import { handleSendGeneric } from 'services/generic';
import { WhatsappMessageModal } from './WhatsappMessageModal';

const STATUS_BADGE = {
  PENDING:   { color: 'warning',   label: 'Pendente'   },
  SENT:      { color: 'success',   label: 'Enviado'    },
  FAILED:    { color: 'danger',    label: 'Falhou'     },
  CANCELLED: { color: 'secondary', label: 'Cancelado'  },
};

const TYPE_LABEL = {
  CHARGE:   'Cobrança',
  REMINDER: 'Lembrete',
  CUSTOM:   'Personalizada',
};

function formatDateTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR');
}

export function WhatsappMessages() {
  const [messages, setMessages]     = useState([]);
  const [clients, setClients]       = useState([]);
  const [modalOpen, setModalOpen]   = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: null });
  const [retryingAll, setRetryingAll] = useState(false);

  useEffect(() => {
    loadMessages();
    loadClients();
  }, []);

  const loadMessages = async () => {
    const data = await getWhatsappMessages();
    setMessages(data);
  };

  const loadClients = async () => {
    const data = await handleSendGeneric('/v1/client', 'GET');
    setClients(data || []);
  };

  const handleSave = async (payload) => {
    const { sendNow: immediate, ...rest } = payload;
    const result = await scheduleWhatsappMessage(rest);
    if (result) {
      if (immediate) {
        await sendWhatsappMessageNow(result.id);
      }
      setModalOpen(false);
      loadMessages();
    }
  };

  const handleCancel = async (id) => {
    await cancelWhatsappMessage(id);
    loadMessages();
  };

  const handleSendNow = async (id) => {
    await sendWhatsappMessageNow(id);
    loadMessages();
  };

  const handleRetry = async (id) => {
    await retryWhatsappMessage(id);
    loadMessages();
  };

  const handleRetryAll = async () => {
    setRetryingAll(true);
    await retryAllFailedMessages();
    setRetryingAll(false);
    loadMessages();
  };

  const openErrorModal = (msg) => setErrorModal({ open: true, message: msg });
  const closeErrorModal = () => setErrorModal({ open: false, message: null });

  const failedCount = messages.filter((m) => m.status === 'FAILED').length;

  return (
    <>
      <Header />
      <Container className='mt--7' fluid>
        <Row>
          <Col>
            <Card className='shadow'>
              <CardHeader className='border-0'>
                <Row className='align-items-center'>
                  <Col>
                    <h3 className='mb-0'>Mensagens WhatsApp</h3>
                  </Col>
                  <Col className='text-right'>
                    {failedCount > 0 && (
                      <Button
                        color='danger'
                        size='sm'
                        className='mr-2'
                        onClick={handleRetryAll}
                        disabled={retryingAll}
                      >
                        {retryingAll
                          ? 'Reenviando...'
                          : `Reenviar com falha (${failedCount})`}
                      </Button>
                    )}
                    <Button
                      color='success'
                      size='sm'
                      onClick={() => setModalOpen(true)}
                    >
                      + Nova Mensagem
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <Table responsive hover>
                <thead className='thead-light'>
                  <tr>
                    <th>Cliente</th>
                    <th>Telefone</th>
                    <th>Tipo</th>
                    <th>Mensagem</th>
                    <th>Agendado para</th>
                    <th>Enviado em</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.length === 0 ? (
                    <tr>
                      <td colSpan='8' className='text-center text-muted py-4'>
                        Nenhuma mensagem agendada.
                      </td>
                    </tr>
                  ) : (
                    messages.map((msg) => {
                      const badge = STATUS_BADGE[msg.status] || { color: 'light', label: msg.status };
                      return (
                        <tr key={msg.id}>
                          <td>{msg.clientName}</td>
                          <td>{msg.clientPhone || '—'}</td>
                          <td>{TYPE_LABEL[msg.messageType] || msg.messageType}</td>
                          <td
                            style={{
                              maxWidth: '250px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={msg.messageBody}
                          >
                            {msg.hasAttachment && (
                              <i className='ni ni-attachment-2 mr-1 text-muted' />
                            )}
                            {msg.messageBody}
                          </td>
                          <td>{formatDateTime(msg.scheduledAt)}</td>
                          <td>{formatDateTime(msg.sentAt)}</td>
                          <td>
                            <Badge color={badge.color}>{badge.label}</Badge>
                            {msg.errorMessage && (
                              <i
                                className='ni ni-notification-70 text-danger ml-2'
                                title='Ver detalhes do erro'
                                style={{ cursor: 'pointer', fontSize: '14px' }}
                                onClick={() => openErrorModal(msg)}
                              />
                            )}
                          </td>
                          <td>
                            {msg.status === 'PENDING' && (
                              <>
                                <Button
                                  color='success'
                                  size='sm'
                                  className='mr-1'
                                  onClick={() => handleSendNow(msg.id)}
                                >
                                  ➤ Enviar
                                </Button>
                                <Button
                                  color='danger'
                                  size='sm'
                                  onClick={() => handleCancel(msg.id)}
                                >
                                  ✕ Cancelar
                                </Button>
                              </>
                            )}
                            {msg.status === 'FAILED' && (
                              <Button
                                color='warning'
                                size='sm'
                                onClick={() => handleRetry(msg.id)}
                              >
                                ↺ Reenviar
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal de detalhe do erro */}
      <Modal isOpen={errorModal.open} toggle={closeErrorModal} centered>
        <ModalHeader toggle={closeErrorModal} className='bg-danger text-white'>
          Falha no envio
        </ModalHeader>
        <ModalBody>
          {errorModal.message && (
            <>
              <p className='mb-1'>
                <strong>Cliente:</strong> {errorModal.message.clientName}
              </p>
              <p className='mb-1'>
                <strong>Telefone:</strong> {errorModal.message.clientPhone || '—'}
              </p>
              <p className='mb-1'>
                <strong>Mensagem:</strong> {errorModal.message.messageBody}
              </p>
              <p className='mb-1'>
                <strong>Agendado para:</strong> {formatDateTime(errorModal.message.scheduledAt)}
              </p>
              <hr />
              <p className='mb-0 text-danger'>
                <strong>Erro:</strong> {errorModal.message.errorMessage}
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={closeErrorModal}>
            Fechar
          </Button>
          <Button
            color='warning'
            onClick={() => {
              handleRetry(errorModal.message.id);
              closeErrorModal();
            }}
          >
            Tentar novamente
          </Button>
        </ModalFooter>
      </Modal>

      <WhatsappMessageModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        onSave={handleSave}
        clients={clients}
      />
    </>
  );
}
